import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Source,
  Layer,
  Marker,
  MapLayerMouseEvent,
  useMap,
} from 'react-map-gl';
import { useAtom } from 'jotai';
import mapboxgl from 'mapbox-gl';

import { measurePointsAtom, measureFinishedAtom } from './atoms';
import { formatNumber } from 'utils/number';

const POINT_LAYER_ID = 'measure-points';
const MIDPOINT_LAYER_ID = 'measure-midpoints';
const LINE_HIT_LAYER_ID = 'measure-line-hit';
const INTERACTIVE_LAYERS = [
  POINT_LAYER_ID,
  MIDPOINT_LAYER_ID,
  LINE_HIT_LAYER_ID,
];
const METERS_TO_FEET = 3.28084;
const FEET_PER_MILE = 5280;
const MILES_THRESHOLD = 52_800;
const SQ_FEET_PER_ACRE = 43_560;
const LINE_COLOR = '#000000';
const TEXT_COLOR = '#161616';

function segmentFeet(a: [number, number], b: [number, number]) {
  return (
    new mapboxgl.LngLat(a[0], a[1]).distanceTo(
      new mapboxgl.LngLat(b[0], b[1])
    ) * METERS_TO_FEET
  );
}

function computeTotalFeet(points: [number, number][]) {
  let total = 0;
  for (let i = 1; i < points.length; i++) {
    total += segmentFeet(points[i - 1], points[i]);
  }
  return total;
}

function computeAreaSqFeet(polygon: [number, number][]) {
  if (polygon.length < 3) return 0;

  const origin = polygon[0];
  const metersPerDegLat = 111320;
  const metersPerDegLng = 111320 * Math.cos((origin[1] * Math.PI) / 180);

  const local = polygon.map(([lng, lat]) => [
    (lng - origin[0]) * metersPerDegLng,
    (lat - origin[1]) * metersPerDegLat,
  ]);

  // Shoelace formula
  let area = 0;
  for (let i = 0; i < local.length; i++) {
    const j = (i + 1) % local.length;
    area += local[i][0] * local[j][1];
    area -= local[j][0] * local[i][1];
  }

  return (Math.abs(area) / 2) * METERS_TO_FEET * METERS_TO_FEET;
}

function formatDistance(feet: number) {
  if (feet >= MILES_THRESHOLD) {
    return `${formatNumber(feet / FEET_PER_MILE, 2)} mi`;
  }
  return `${formatNumber(Math.round(feet), 0)} ft`;
}

function formatArea(sqFeet: number) {
  const acres = sqFeet / SQ_FEET_PER_ACRE;
  if (acres >= 640) {
    return `${formatNumber(acres / 640, 2)} sq mi`;
  }
  return `${formatNumber(acres, 2)} acres`;
}

function formatAcres(sqFeet: number) {
  return `${formatNumber(sqFeet / SQ_FEET_PER_ACRE, 2)} acres`;
}

function midpoint(a: [number, number], b: [number, number]): [number, number] {
  return [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
}

/**
 * Find which segment [i, i+1] a point is closest to.
 * @param {[number, number]} coord - The coordinate to test
 * @param {[number, number][]} pts - The array of polyline vertices
 * @param {boolean} isClosed - Whether the polyline forms a closed polygon
 * @return {number} The index at which to insert a new vertex
 */
function findInsertIndex(
  coord: [number, number],
  pts: [number, number][],
  isClosed: boolean
): number {
  let bestDist = Infinity;
  let bestIdx = 1;
  const len = isClosed ? pts.length : pts.length - 1;
  for (let i = 0; i < len; i++) {
    const a = pts[i];
    const b = pts[(i + 1) % pts.length];
    // Project coord onto segment a→b
    const dx = b[0] - a[0];
    const dy = b[1] - a[1];
    const lenSq = dx * dx + dy * dy;
    if (lenSq === 0) continue;
    const t = Math.max(
      0,
      Math.min(1, ((coord[0] - a[0]) * dx + (coord[1] - a[1]) * dy) / lenSq)
    );
    const px = a[0] + t * dx;
    const py = a[1] + t * dy;
    const dist = (coord[0] - px) ** 2 + (coord[1] - py) ** 2;
    if (dist < bestDist) {
      bestDist = dist;
      bestIdx = i + 1;
    }
  }
  return bestIdx;
}

const labelStyle: React.CSSProperties = {
  fontFamily: "'IBM Plex Sans', 'Helvetica Neue', Arial, sans-serif",
  whiteSpace: 'nowrap',
  pointerEvents: 'none',
  lineHeight: 1,
};

const segmentLabelStyle: React.CSSProperties = {
  ...labelStyle,
  background: 'white',
  border: `1px solid ${LINE_COLOR}`,
  borderRadius: 2,
  padding: '2px 6px',
  color: TEXT_COLOR,
  fontSize: 15,
  fontWeight: 400,
};

const totalLabelStyle: React.CSSProperties = {
  ...labelStyle,
  background: LINE_COLOR,
  borderRadius: 2,
  padding: '4px 10px',
  color: 'white',
  fontSize: 16,
  fontWeight: 600,
};

const areaBoxStyle: React.CSSProperties = {
  ...labelStyle,
  position: 'absolute',
  top: 10,
  left: 10,
  zIndex: 5,
  background: 'white',
  border: `1px solid ${LINE_COLOR}`,
  borderRadius: 4,
  padding: '8px 12px',
  color: TEXT_COLOR,
  boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
};

export const MeasureControl = () => {
  const { current: mapRef } = useMap();
  const [points, setPoints] = useAtom(measurePointsAtom);
  const [finished, setFinished] = useAtom(measureFinishedAtom);
  const [closed, setClosed] = useState(false);
  const [cursor, setCursor] = useState<[number, number] | null>(null);
  const draggingIdx = useRef<number | null>(null);
  const didDrag = useRef(false);
  // Pending midpoint insert — deferred until first mousemove so mouseup
  // doesn't immediately cancel the drag before it starts
  const pendingMidInsert = useRef<{
    insertIdx: number;
    coord: [number, number];
  } | null>(null);

  const onClick = useCallback(
    (e: MapLayerMouseEvent) => {
      // If an actual drag happened, don't process as a click
      if (didDrag.current) {
        didDrag.current = false;
        return;
      }

      const map = mapRef?.getMap();
      if (!map) return;

      // Check vertices first
      const vertexFeatures = map.queryRenderedFeatures(e.point, {
        layers: [POINT_LAYER_ID],
      });

      if (vertexFeatures && vertexFeatures.length > 0) {
        const clickedIdx = vertexFeatures[0].properties?.idx as number;

        if (!finished) {
          // Clicking the first point with 3+ points closes the polygon
          if (clickedIdx === 0 && points.length >= 3) {
            setClosed(true);
            setFinished(true);
            setCursor(null);
            return;
          }

          // Clicking the last point with 2+ points finishes the line
          if (clickedIdx === points.length - 1 && points.length >= 2) {
            setFinished(true);
            setCursor(null);
            return;
          }
        }

        // Remove the point (works in both active and finished states)
        setPoints((prev) => {
          const next = prev.filter((_, i) => i !== clickedIdx);
          // If closed polygon drops below 3 points, revert to open line
          if (closed && next.length < 3) {
            setClosed(false);
            setFinished(false);
          }
          return next;
        });
        return;
      }

      // Only add new points when not finished
      if (!finished) {
        setPoints((prev) => [...prev, [e.lngLat.lng, e.lngLat.lat]]);
      }
    },
    [mapRef, setPoints, setFinished, finished, closed, points.length]
  );

  const onMouseDown = useCallback(
    (e: MapLayerMouseEvent) => {
      const map = mapRef?.getMap();
      if (!map) return;

      // Check vertices
      const vertexFeatures = map.queryRenderedFeatures(e.point, {
        layers: [POINT_LAYER_ID],
      });
      if (vertexFeatures && vertexFeatures.length > 0) {
        draggingIdx.current = vertexFeatures[0].properties?.idx as number;
        map.getCanvas().style.cursor = 'grabbing';
        map.dragPan.disable();
        e.preventDefault();
        return;
      }

      // Check midpoints or line — defer insert until first mousemove
      const midFeatures = map.queryRenderedFeatures(e.point, {
        layers: [MIDPOINT_LAYER_ID, LINE_HIT_LAYER_ID],
      });
      if (midFeatures && midFeatures.length > 0) {
        const coord: [number, number] = [e.lngLat.lng, e.lngLat.lat];
        // Midpoint has explicit insertIdx; line hit needs calculation
        const feat = midFeatures.find((f) => f.layer.id === MIDPOINT_LAYER_ID);
        const insertIdx = feat
          ? (feat.properties?.insertIdx as number)
          : findInsertIndex(coord, points, closed);
        pendingMidInsert.current = { insertIdx, coord };
        map.getCanvas().style.cursor = 'grabbing';
        map.dragPan.disable();
        e.preventDefault();
      }
    },
    [mapRef, points, closed]
  );

  const onMouseUp = useCallback(() => {
    const map = mapRef?.getMap();
    // If there was a pending midpoint insert that never dragged, discard it
    if (pendingMidInsert.current) {
      pendingMidInsert.current = null;
      if (map) {
        map.dragPan.enable();
        map.getCanvas().style.cursor = 'pointer';
      }
      return;
    }
    if (draggingIdx.current === null) return;
    if (map) {
      map.dragPan.enable();
      map.getCanvas().style.cursor = 'pointer';
    }
    draggingIdx.current = null;
  }, [mapRef]);

  const onMouseMove = useCallback(
    (e: MapLayerMouseEvent) => {
      const map = mapRef?.getMap();
      if (!map) return;

      // Commit pending midpoint insert on first move
      if (pendingMidInsert.current) {
        const { insertIdx, coord } = pendingMidInsert.current;
        pendingMidInsert.current = null;
        setPoints((prev) => [
          ...prev.slice(0, insertIdx),
          coord,
          ...prev.slice(insertIdx),
        ]);
        draggingIdx.current = insertIdx;
        didDrag.current = true;
      }

      // Handle vertex dragging
      if (draggingIdx.current !== null) {
        didDrag.current = true;
        const idx = draggingIdx.current;
        setPoints((prev) =>
          prev.map((p, i) => (i === idx ? [e.lngLat.lng, e.lngLat.lat] : p))
        );
        return;
      }

      if (!finished) {
        setCursor([e.lngLat.lng, e.lngLat.lat]);
      }

      const features = map.queryRenderedFeatures(e.point, {
        layers: INTERACTIVE_LAYERS,
      });
      if (features && features.length > 0) {
        map.getCanvas().style.cursor = 'pointer';
      } else {
        map.getCanvas().style.cursor = finished ? '' : 'crosshair';
      }
    },
    [mapRef, finished, setPoints]
  );

  useEffect(() => {
    const map = mapRef?.getMap();
    if (!map) return;

    map.on('click', onClick);
    map.on('mousedown', onMouseDown);
    map.on('mouseup', onMouseUp);
    map.on('mousemove', onMouseMove);

    map.getCanvas().style.cursor = finished ? '' : 'crosshair';

    return () => {
      map.off('click', onClick);
      map.off('mousedown', onMouseDown);
      map.off('mouseup', onMouseUp);
      map.off('mousemove', onMouseMove);
      map.getCanvas().style.cursor = '';
      map.dragPan.enable();
    };
  }, [mapRef, onClick, onMouseDown, onMouseUp, onMouseMove, finished]);

  const showPreview = !finished && cursor && points.length >= 1;
  const displayPoints = useMemo(
    () => (showPreview ? [...points, cursor] : points),
    [showPreview, points, cursor]
  );

  // For a closed polygon, connect back to the first point
  const lineCoords = useMemo(
    () => (closed ? [...points, points[0]] : displayPoints),
    [closed, points, displayPoints]
  );

  const lineGeoJson = useMemo<GeoJSON.Feature<GeoJSON.LineString>>(
    () => ({
      type: 'Feature',
      properties: {},
      geometry: {
        type: 'LineString',
        coordinates: lineCoords,
      },
    }),
    [lineCoords]
  );

  const polygonGeoJson =
    useMemo<GeoJSON.Feature<GeoJSON.Polygon> | null>(() => {
      if (!closed) return null;
      return {
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'Polygon',
          coordinates: [[...points, points[0]]],
        },
      };
    }, [closed, points]);

  const pointsGeoJson = useMemo<GeoJSON.FeatureCollection>(
    () => ({
      type: 'FeatureCollection',
      features: points.map((coord, idx) => ({
        type: 'Feature' as const,
        properties: { idx },
        geometry: {
          type: 'Point' as const,
          coordinates: coord,
        },
      })),
    }),
    [points]
  );

  // Midpoints between consecutive vertices (draggable to insert new vertex)
  const midpointsGeoJson = useMemo<GeoJSON.FeatureCollection>(() => {
    const mids: GeoJSON.Feature[] = [];
    const pairs = closed
      ? [...points.map((_, i) => i), 0]
      : points.map((_, i) => i);
    for (let i = 0; i < pairs.length - 1; i++) {
      const a = points[pairs[i]];
      const b = points[pairs[i + 1]];
      mids.push({
        type: 'Feature',
        properties: { insertIdx: pairs[i] + 1 },
        geometry: {
          type: 'Point',
          coordinates: midpoint(a, b),
        },
      });
    }
    return { type: 'FeatureCollection', features: mids };
  }, [closed, points]);

  const perimeterFeet = computeTotalFeet(lineCoords);
  const lastDisplayPoint = displayPoints[displayPoints.length - 1];

  // Build segment labels
  const segments: { mid: [number, number]; feet: number }[] = [];
  for (let i = 1; i < lineCoords.length; i++) {
    segments.push({
      mid: midpoint(lineCoords[i - 1], lineCoords[i]),
      feet: segmentFeet(lineCoords[i - 1], lineCoords[i]),
    });
  }

  const areaSqFeet = closed ? computeAreaSqFeet(points) : 0;

  return (
    <>
      {closed && polygonGeoJson && (
        <Source id="measure-polygon" type="geojson" data={polygonGeoJson}>
          <Layer
            id="measure-polygon-fill"
            type="fill"
            paint={{
              'fill-color': LINE_COLOR,
              'fill-opacity': 0.08,
            }}
          />
        </Source>
      )}
      {lineCoords.length >= 2 && (
        <Source id="measure-line" type="geojson" data={lineGeoJson}>
          {/* Invisible wide line for easy click/drag targeting */}
          <Layer
            id={LINE_HIT_LAYER_ID}
            type="line"
            paint={{
              'line-color': LINE_COLOR,
              'line-width': 16,
              'line-opacity': 0,
            }}
          />
          <Layer
            id="measure-line-layer"
            type="line"
            paint={{
              'line-color': LINE_COLOR,
              'line-width': 2,
            }}
          />
        </Source>
      )}
      <Source id="measure-points-source" type="geojson" data={pointsGeoJson}>
        <Layer
          id={POINT_LAYER_ID}
          type="circle"
          paint={{
            'circle-radius': 4,
            'circle-color': 'white',
            'circle-stroke-color': LINE_COLOR,
            'circle-stroke-width': 2,
          }}
        />
      </Source>
      {points.length >= 2 && (
        <Source
          id="measure-midpoints-source"
          type="geojson"
          data={midpointsGeoJson}
        >
          <Layer
            id={MIDPOINT_LAYER_ID}
            type="circle"
            paint={{
              'circle-radius': 3,
              'circle-color': LINE_COLOR,
              'circle-opacity': 0.25,
              'circle-stroke-width': 0,
            }}
          />
        </Source>
      )}
      {segments.map((seg, i) => (
        <Marker
          key={`seg-${i}`}
          longitude={seg.mid[0]}
          latitude={seg.mid[1]}
          anchor="bottom"
          offset={[0, -6]}
        >
          <div style={segmentLabelStyle}>{formatDistance(seg.feet)}</div>
        </Marker>
      ))}
      {!closed && lineCoords.length >= 2 && lastDisplayPoint && (
        <Marker
          longitude={lastDisplayPoint[0]}
          latitude={lastDisplayPoint[1]}
          anchor="bottom-left"
          offset={[10, -10]}
        >
          <div style={totalLabelStyle}>{formatDistance(perimeterFeet)}</div>
        </Marker>
      )}
      {closed && (
        <div style={areaBoxStyle}>
          <div style={{ fontSize: 16, fontWeight: 600 }}>
            {formatArea(areaSqFeet)}
          </div>
          {areaSqFeet / SQ_FEET_PER_ACRE >= 640 && (
            <div style={{ fontSize: 13, fontWeight: 400, marginTop: 2 }}>
              {formatAcres(areaSqFeet)}
            </div>
          )}
          <div style={{ fontSize: 13, fontWeight: 400, marginTop: 2 }}>
            {formatDistance(perimeterFeet)} perimeter
          </div>
        </div>
      )}
    </>
  );
};
